// The palette every figure on these reports draws at, matching the portal's own.
export const INK = "#202338";
export const INK_2 = "#5B6D7A";
export const WARM = "#E2772E";
export const LINE = "#5B6D7A80";

// The head figures all share one canvas, so three of them side by side line up with each other
// however differently each one is cropped.
export const HEAD_CENTRE_X = 60;

/**
 * How large a caption can be drawn before it runs out of its box.
 *
 * The caption sits on one line under the head and is clipped at both ends rather than wrapped,
 * so the size has to follow the text. Verdana bold runs about 0.72em per upper-case character.
 */
export const fitCaptionFontSize = (
  text: string,
  box = 112,
  base = 9,
  perCharacter = 0.72,
  tracking = 0.4
): number => {
  const count = Math.max(text.length, 1);
  return Math.max(
    5.6,
    Math.min(base, (box - tracking * count) / (perCharacter * count))
  );
};

export const BoldCaption = ({
  text,
  y = 122,
}: {
  text: string;
  y?: number;
}) => {
  const upper = text.toUpperCase();

  return (
    <text
      x={HEAD_CENTRE_X}
      y={y}
      fontSize={fitCaptionFontSize(upper)}
      fontWeight={700}
      letterSpacing="0.4"
      fill={INK}
      textAnchor="middle"
    >
      {upper}
    </text>
  );
};

/**
 * The head seen from above, with the nose pointing up the page.
 *
 * Figures drawn from above are NOT mirrored: the face points away from the reader, so the
 * patient's left eye is on the reader's left. The facing-forward head below is the opposite
 * case, and the two being different is deliberate rather than an inconsistency to tidy up.
 */
export const BoldHeadAbove = () => (
  <>
    <ellipse cx={HEAD_CENTRE_X} cy={84} rx={33} ry={21} fill={INK} />
    <path
      d="M53 69 L60 56 L67 69 Z"
      fill={INK}
      stroke={INK}
      strokeWidth={2}
      strokeLinejoin="round"
    />
  </>
);

/**
 * The head seen face on: tapered jaw, ears and a nose.
 *
 * Figures drawn face on ARE mirrored, because the patient is looking at the reader: the
 * patient's left eye belongs on the reader's RIGHT. Every caller that labels one eye has to
 * honour that, and it is the kind of thing that gets "fixed" later -- see `BoldHeadAbove` for
 * the case where it does not apply.
 */
export const BoldHeadFront = ({ nose = true }: { nose?: boolean }) => (
  <>
    <ellipse cx={31} cy={60} rx={5.5} ry={8.5} fill={INK} />
    <ellipse cx={89} cy={60} rx={5.5} ry={8.5} fill={INK} />
    <path
      d="M60 24 C77 24 88 37 88 53 C88 73 76 93 60 93 C44 93 32 73 32 53 C32 37 43 24 60 24 Z"
      fill={INK}
    />
    {nose && (
      <path
        d="M60 56 L60 70 L65.5 70"
        fill="none"
        stroke="#FFFFFF"
        strokeOpacity={0.32}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </>
);

// Straight, with a solid head. The horizontal arrow is the vertical one turned ninety degrees,
// so every arrow on these reports is the same arrow.
export const BoldArrowVertical = ({
  x,
  from,
  to,
}: {
  x: number;
  from: number;
  to: number;
}) => {
  const direction = to > from ? 1 : -1;

  return (
    <>
      <line
        x1={x}
        y1={from}
        x2={x}
        y2={to - direction * 5}
        stroke={WARM}
        strokeWidth={2.8}
        strokeLinecap="round"
      />
      <path d={`M${x} ${to} l-4.4 ${-direction * 7} l8.8 0 Z`} fill={WARM} />
    </>
  );
};

export const BoldArrowHorizontal = ({
  y,
  from,
  to,
}: {
  y: number;
  from: number;
  to: number;
}) => {
  const direction = to > from ? 1 : -1;

  return (
    <>
      <line
        x1={from}
        y1={y}
        x2={to - direction * 5}
        y2={y}
        stroke={WARM}
        strokeWidth={2.8}
        strokeLinecap="round"
      />
      <path d={`M${to} ${y} l${-direction * 7} -4.4 l0 8.8 Z`} fill={WARM} />
    </>
  );
};
